import { useState } from "react";

/**
 * EMRKT.CH — Dynamic brand mark.
 *
 * A pulsing 3-bar "market signal" glyph framed in a soft rounded square.
 * The bars represent the autonomous commerce loop (discover → price → sell),
 * the top-right spark represents the AI orchestrator.
 *
 * Usage:
 *   <BrandMark size={28} />              // static (sidebar, login, footer)
 *   <BrandMark size={64} animated />     // hero/landing (ambient pulse)
 */
export function BrandMark({ size = 28, animated = false, className = "" }) {
  const [hover, setHover] = useState(false);
  const active = animated || hover;
  const uid = `bm-${size}-${animated ? "a" : "s"}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      xmlns="http://www.w3.org/2000/svg"
      className={`brand-mark ${className}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      role="img"
      aria-label="EMRKT.CH"
      data-testid="brand-mark"
    >
      <defs>
        <linearGradient id={`${uid}-bg`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0a1520" />
          <stop offset="100%" stopColor="#050505" />
        </linearGradient>
        <linearGradient id={`${uid}-bar`} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#0891b2" />
          <stop offset="100%" stopColor="#22d3ee" />
        </linearGradient>
        <linearGradient id={`${uid}-tall`} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#22d3ee" />
          <stop offset="100%" stopColor="#e0f7ff" />
        </linearGradient>
        <filter id={`${uid}-glow`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Frame */}
      <rect
        x="1.5"
        y="1.5"
        width="37"
        height="37"
        rx="8"
        fill={`url(#${uid}-bg)`}
        stroke="#22d3ee"
        strokeOpacity={active ? 0.9 : 0.55}
        strokeWidth="1.25"
        style={{ transition: "stroke-opacity 300ms ease" }}
      />

      {/* Bars — grow from bottom */}
      <g filter={`url(#${uid}-glow)`}>
        <rect
          x="8"
          y="22"
          width="5"
          height="10"
          rx="1"
          fill={`url(#${uid}-bar)`}
          style={{
            transformOrigin: "8px 32px",
            transform: active ? "scaleY(1)" : "scaleY(0.7)",
            transition: "transform 500ms cubic-bezier(0.34, 1.56, 0.64, 1)",
          }}
        />
        <rect
          x="17.5"
          y="14"
          width="5"
          height="18"
          rx="1"
          fill={`url(#${uid}-tall)`}
          style={{
            transformOrigin: "17.5px 32px",
            transform: active ? "scaleY(1)" : "scaleY(0.55)",
            transition: "transform 600ms cubic-bezier(0.34, 1.56, 0.64, 1) 80ms",
          }}
        />
        <rect
          x="27"
          y="18"
          width="5"
          height="14"
          rx="1"
          fill={`url(#${uid}-bar)`}
          style={{
            transformOrigin: "27px 32px",
            transform: active ? "scaleY(1)" : "scaleY(0.65)",
            transition: "transform 550ms cubic-bezier(0.34, 1.56, 0.64, 1) 160ms",
          }}
        />
      </g>

      {/* AI spark */}
      <circle
        cx="31"
        cy="10"
        r="1.7"
        fill="#22d3ee"
        style={{
          opacity: active ? 1 : 0.7,
          transition: "opacity 250ms ease",
        }}
      >
        {animated && (
          <animate
            attributeName="r"
            values="1.4;2.2;1.4"
            dur="2.2s"
            repeatCount="indefinite"
          />
        )}
      </circle>
      <circle
        cx="31"
        cy="10"
        r="3.5"
        fill="none"
        stroke="#22d3ee"
        strokeOpacity="0.35"
        strokeWidth="0.8"
        style={{
          opacity: active ? 1 : 0,
          transition: "opacity 300ms ease",
        }}
      />
    </svg>
  );
}
